import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Award, Users, Calendar, BookOpen, Heart, Target, Quote, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import aboutPhoto from '../assets/hero.png';

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 25 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const AboutSection = () => {
  const { aboutData, theme } = useLanguage();
  const isDark = theme === 'dark';
  const icons = [Award, Users, Calendar, BookOpen];
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const photoY = useSpring(useTransform(scrollYProgress, [0, 1], ['5%', '-5%']), { stiffness: 60, damping: 20 });
  const blobY = useSpring(useTransform(scrollYProgress, [0, 1], ['-10%', '10%']), { stiffness: 40, damping: 15 });

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-24 md:py-36 relative overflow-hidden"
      style={{ background: isDark ? 'var(--bg-page)' : '#FFFFFF' }}
    >
      {/* Decorative Parallax Background Element */}
      <motion.div
        style={{
          position: 'absolute',
          top: '15%',
          right: '-5%',
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, var(--accent-gold-200), transparent)',
          opacity: isDark ? 0.2 : 0.4,
          pointerEvents: 'none',
          zIndex: 0,
          y: blobY,
        }}
      />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          
          {/* ── Photo Column (5/12) ─── */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] md:aspect-auto md:h-[650px]">
              
              {/* Back Frame - Glass look */}
              <motion.div
                className="absolute -top-6 -left-6 w-full h-full rounded-2xl border border-gold-subtle"
                style={{ 
                  background: isDark ? 'rgba(184,134,11,0.05)' : 'rgba(184,134,11,0.03)',
                  backdropFilter: 'blur(5px)'
                }}
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, 1, 0]
                }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Main Photo Wrapper */}
              <div className="relative h-full w-full rounded-2xl overflow-hidden border border-divider shadow-[0_32px_64px_rgba(0,0,0,0.1)]">
                <motion.div style={{ y: photoY, height: '110%', width: '100%', position: 'absolute', top: '-5%' }}>
                  <img
                    src={aboutPhoto}
                    alt="Lucimar Bizoni"
                    className="w-full h-full object-cover object-top"
                    style={{
                      maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
                    }}
                  />
                </motion.div>
                
                {/* Overlay Vignette */}
                <div className="absolute inset-0 pointer-events-none" 
                  style={{ background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.4) 100%)' }} />
              </div>

              {/* Floating Quote Card */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="absolute -bottom-8 -right-4 md:-right-10 p-8 rounded-2xl shadow-2xl max-w-[280px]"
                style={{ 
                  background: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(184,134,11,0.25)',
                  zIndex: 20 
                }}
              >
                <div className="absolute top-0 left-8 transform -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-gold text-white shadow-lg">
                  <Quote size={20} fill="currentColor" />
                </div>
                <p className="font-serif italic text-lg leading-relaxed mb-4" style={{ color: 'var(--text-primary)' }}>
                  "Minha missão é orientar, inspirar e transformar vidas pelos princípios bíblicos."
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-px bg-gold" />
                  <p className="mono-text text-[0.65rem] tracking-widest uppercase">Lucimar Bizoni</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* ── Content Column (7/12) ─── */}
          <div className="lg:col-span-7">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <motion.div variants={item} className="mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full mb-6"
                  style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)' }}
                >
                  <Sparkles size={14} className="text-gold" />
                  <span className="mono-text text-gold" style={{ fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                    SOBRE A MENTORA
                  </span>
                </div>
                <h2 className="heading-hero mb-8">{aboutData.title}</h2>
                <div className="space-y-6">
                  {aboutData.bio.map((paragraph, i) => (
                    <p
                      key={i}
                      className={i === 0 ? 'text-xl font-serif' : 'text-lg leading-relaxed'}
                      style={{ 
                        color: i === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontStyle: i === 0 ? 'italic' : 'normal'
                      }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>

              {/* Roles Badges */}
              <motion.div variants={item} className="flex flex-wrap gap-3 mb-10">
                {aboutData.roles.map((role, i) => (
                  <div key={i} className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      background: isDark ? 'rgba(212,175,55,0.08)' : 'rgba(0,0,0,0.02)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {role}
                  </div>
                ))}
              </motion.div>

              {/* Experience Highlights - Premium Mini Cards */}
              <motion.div variants={item} className="grid grid-cols-2 gap-4 mb-10">
                {aboutData.experience.map((stat, i) => {
                  const IconComp = icons[i % icons.length];
                  return (
                    <motion.div
                      key={i}
                      whileHover={{ y: -5 }}
                      className="p-5 rounded-xl transition-all"
                      style={{ 
                        background: isDark ? 'rgba(212,175,55,0.06)' : 'rgba(0,0,0,0.02)',
                        border: '1px solid var(--border-primary)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <IconComp size={20} style={{ color: 'var(--accent-gold-400)', marginBottom: '0.75rem' }} />
                      <h4 className="font-bold mb-1" style={{ fontSize: '1rem', color: 'var(--text-primary)' }}>{stat.title}</h4>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{stat.description}</p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Mission Statement - High Impact Block */}
              <motion.div
                variants={item}
                className="relative p-10 rounded-3xl overflow-hidden shadow-xl"
                style={{ background: isDark ? '#1a1a1a' : '#FAF7F2', border: '1px solid var(--accent-gold-400)' }}
              >
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Target size={120} className="text-gold" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1.5 h-6 bg-gold rounded-full" />
                    <h3 className="mono-text tracking-widest text-gold text-xs">NOSSA MISSÃO</h3>
                  </div>
                  <p className="text-2xl font-serif leading-tight italic" style={{ color: 'var(--text-primary)' }}>
                    "{aboutData.mission}"
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
